/*
 * Figma `122:617` — TODAY-05 · Calendar & Milestones.
 *
 * This destination is the industry calendar: other people's concerts, so nobody books a weekend
 * that already has three festivals in it. The frame titles it "Calendar & Milestones" and draws
 * something else — milestones, task deadlines and a show schedule on a month grid, with a collision
 * count and a day panel of cues.
 *
 * One of those four still cannot be placed on a grid: cues carry a time of day and no date.
 * Milestones now can — TICK-143 made one a task somebody marked, rather than a second record type,
 * so the dates the event is measured by are already in the plan. And the show itself can:
 * `events.ends_at` reaches the console on the ops settings record, with the day taken in the
 * event's own timezone rather than UTC's, because a show ending just after midnight in Bangkok
 * falls on the previous day in UTC.
 *
 * What was always real is task deadlines, and the concerts that share their day. That is the
 * frame's collision count, asked of the organizer's own dates instead of left to be eyeballed off
 * a grid — and it is the question this destination existed for in the first place. The grid itself
 * is the page that already draws it; rebuilding six hundred lines of month cells to change their
 * border radius would be the "thirty-three one-off layouts" the brief rules out.
 */
import { Suspense, lazy } from 'react';
import {
  Alert, Card, DashboardTemplate, FeedbackState, KpiCard, LoadingRows, StatusBadge,
} from '../..';
import type { Destination } from '../../destinations';
import { adminApi } from '../../../api';
import type { CalendarState, DatedCommitment } from './useEventCalendar';
import { useEventCalendar } from './useEventCalendar';

/* 629 lines with its own tooltip portal and month grid. Off the entry chunk, like Flow Maps. */
const ConcertCalendar = lazy(() => import('../../../pages/ConcertCalendar'));

const monthOf = (iso: string) => iso.slice(0, 7);
const day = (iso: string) => new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });

export function CalendarScreen({ destination, state, permitted, role, token, eventSlug }: {
  destination: Destination;
  state: CalendarState;
  permitted: boolean;
  role?: string | null;
  token: string;
  eventSlug?: string;
}) {
  const { concerts, commitments, loading, loadError, tasksUnavailable, eventEndsAt, reload } = state;

  const thisMonth = monthOf(new Date().toISOString());
  const concertsThisMonth = concerts.filter((c) => c.monthKey === thisMonth).length;
  const clashing = commitments.filter((c) => c.collisions.length > 0);
  const milestones = commitments.filter((c) => c.kind === 'milestone');

  return (
    <DashboardTemplate
      gate={{ destination, permitted, role, loading, error: loadError || null, onRetry: reload }}
      kpis={
        <>
          <KpiCard
            label="คอนเสิร์ตในปฏิทิน"
            value={concerts.length.toLocaleString('th-TH')}
            meta={`เดือนนี้ ${concertsThisMonth.toLocaleString('th-TH')} งาน`}
          />
          <KpiCard
            label="Milestone"
            value={tasksUnavailable ? 'ไม่มีสิทธิ์ดู' : milestones.length.toLocaleString('th-TH')}
            meta={tasksUnavailable
              ? 'แผนงานเป็นข้อมูลของทีมปฏิบัติการ'
              : milestones.length > 0
                ? `ผ่านไปแล้ว ${milestones.filter((m) => m.done).length} · วันที่ใช้วัดความคืบหน้าของงานนี้`
                : 'ยังไม่มีงานไหนถูกทำเครื่องหมายเป็น milestone'}
          />
          <KpiCard
            label="กำหนดส่งที่ยังค้าง"
            value={tasksUnavailable ? 'ไม่มีสิทธิ์ดู' : commitments.filter((c) => c.kind === 'task').length.toLocaleString('th-TH')}
            meta={tasksUnavailable
              ? 'แผนงานเป็นข้อมูลของทีมปฏิบัติการ'
              : 'นับเฉพาะงานที่ยังไม่เสร็จและมีวันกำหนดส่ง'}
          />
          <KpiCard
            label="วันที่ชนกับคอนเสิร์ตอื่น"
            value={tasksUnavailable ? '—' : clashing.length.toLocaleString('th-TH')}
            tone={clashing.length > 0 ? 'warning' : 'neutral'}
            meta={clashing.length > 0
              ? 'มีคอนเสิร์ตอื่นจัดวันเดียวกับกำหนดส่งของเรา'
              : 'ยังไม่มีวันไหนของเราชนกับคอนเสิร์ตในปฏิทิน'}
          />

        </>
      }
      actions={
        <Card
          title="วันสำคัญของงานนี้"
          sub="Milestone และกำหนดส่งที่ยังค้าง เรียงตามวัน พร้อมคอนเสิร์ตที่จัดวันเดียวกัน"
        >
          {tasksUnavailable ? (
            <p className="jt-field-hint">
              Token นี้อ่านแผนงานไม่ได้ — ปฏิทินคอนเสิร์ตด้านล่างยังดูได้ตามปกติ
            </p>
          ) : commitments.length === 0 ? (
            <FeedbackState state="empty" title="ยังไม่มีวันกำหนดส่งที่ค้างอยู่">
              งานที่ยังไม่เสร็จและมีวันกำหนดส่งจะขึ้นที่นี่ พร้อมบอกว่าวันนั้นมีคอนเสิร์ตอื่นหรือไม่
            </FeedbackState>
          ) : (
            <ul className="jt-checks">
              {commitments.map((item) => <CommitmentRow key={item.id} item={item} />)}
            </ul>
          )}

          {!eventEndsAt && !tasksUnavailable && (
            /* Said on the page rather than only in the ticket: the frame puts SHOW DAY on the grid,
             * and an organizer who cannot find it would otherwise read that as a bug. Hidden for a
             * token that cannot read ops at all — that is a permission, not a missing date. */
            <Alert tone="warning" title="ยังไม่ได้ตั้งวันจบงานของงานนี้">
              วันแสดงจะขึ้นบนรายการนี้เมื่อมีการตั้งวันจบงานแล้ว — ทีมงาน JamTick เป็นคนตั้งให้ ·
              ระหว่างนี้การชนวันด้านบนนับเฉพาะกำหนดส่งของแผนงาน
            </Alert>
          )}
        </Card>
      }
      list={
        <Card
          title="ปฏิทินคอนเสิร์ตในตลาด"
          sub="ข้อมูลภายใน ไม่เปิดสาธารณะ — ใช้ดูว่าช่วงไหนมีงานอื่นอยู่แล้ว"
          flush
        >
          <Suspense fallback={<LoadingRows rows={6} label="ปฏิทินคอนเสิร์ต" />}>
            <ConcertCalendar
              embedded
              fetchData={() => adminApi.getConcertCalendar(token, eventSlug)}
            />
          </Suspense>
        </Card>
      }
    />
  );
}

function CommitmentRow({ item }: { item: DatedCommitment }) {
  return (
    <li className="jt-check">
      <div className="jt-check-copy">
        <p className="jt-check-label">
          {item.kind === 'milestone' && <StatusBadge tone="accent">MILESTONE</StatusBadge>}
          {item.kind === 'milestone' ? ' ' : ''}{item.label}
          {item.kind === 'show' && <span className="jt-check-blocking"> · วันแสดง</span>}
          {item.done && <span className="jt-check-blocking"> · ผ่านไปแล้ว</span>}
        </p>
        <p className="jt-check-detail">
          {day(item.date)}
          {item.owner ? ` · ${item.owner}` : ' · ยังไม่มีเจ้าของ'}
        </p>
        {item.collisions.length > 0 && (
          <p className="jt-check-detail">
            วันเดียวกับ {item.collisions.map((c) => `${c.eventName} (${c.province})`).join(' · ')}
          </p>
        )}
      </div>
      <span className="jt-check-trail">
        <StatusBadge tone={item.collisions.length > 0 ? 'warning' : 'neutral'}>
          {item.collisions.length > 0 ? `ชน ${item.collisions.length}` : 'ไม่ชน'}
        </StatusBadge>
      </span>
    </li>
  );
}

export function CalendarDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useEventCalendar(token, eventSlug, role ?? null);
  return (
    <CalendarScreen
      destination={destination}
      state={state}
      permitted={permitted}
      role={role}
      token={token}
      eventSlug={eventSlug}
    />
  );
}
